// apps/web/src/components/documents/DocumentsManager.tsx
// Documents management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Document } from '../../types/document';
import { apiClient } from '../../lib/apiClient';

export function DocumentsManager() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/documents');
        setDocuments(response.data.documents);
      } catch (err) {
        setError('Failed to load documents');
        console.error('Error loading documents:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const handleUploadDocument = async (documentData: any) => {
    try {
      const response = await apiClient.post('/api/v1/documents', documentData);
      setDocuments([response.data, ...documents]);
      setShowUploadForm(false);
    } catch (err) {
      setError('Failed to upload document');
      console.error('Error uploading document:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading documents...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="documents-manager">
      <div className="header">
        <h2>Documents Management</h2>
        <button 
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="btn btn-primary"
        >
          {showUploadForm ? 'Cancel' : 'Upload Document'}
        </button>
      </div>

      {showUploadForm && (
        <DocumentUploadForm 
          onSubmit={handleUploadDocument} 
          onCancel={() => setShowUploadForm(false)} 
        />
      )}

      <div className="documents-grid">
        {documents.map(document => (
          <DocumentCard key={document.id} document={document} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function DocumentCard({ document }: { document: Document }) {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'PDF': return '📄';
      case 'DOC': return '📝';
      case 'XLS': return '📊';
      case 'PPT': return '📽️';
      case 'IMG': return '🖼️';
      case 'VIDEO': return '🎬';
      default: return '📁';
    }
  };

  return (
    <div className="document-card">
      <div className="document-header">
        <span className="document-icon">{getTypeIcon(document.documentType)}</span>
        <h3>{document.name}</h3>
      </div>
      <p>{document.description || 'No description'}</p>
      <p><strong>Size:</strong> {(document.fileSize / 1024).toFixed(1)} KB</p>
      <p><strong>Type:</strong> {document.documentType}</p>
      <p><strong>Created:</strong> {new Date(document.createdAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-outline">Download</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function DocumentUploadForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    documentType: 'PDF',
    file: null as File | null,
    tags: [] as string[]
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="document-upload-form">
      <h3>Upload New Document</h3>
      <input
        type="text"
        placeholder="Document Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <select
        value={formData.documentType}
        onChange={(e) => setFormData({...formData, documentType: e.target.value})}
      >
        <option value="PDF">PDF</option>
        <option value="DOC">Word Document</option>
        <option value="XLS">Excel Spreadsheet</option>
        <option value="PPT">PowerPoint</option>
        <option value="IMG">Image</option>
        <option value="VIDEO">Video</option>
        <option value="OTHER">Other</option>
      </select>
      <input
        type="file"
        onChange={(e) => setFormData({...formData, file: e.target.files?.[0] || null})}
        required
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Upload Document</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}